/**
 * Minimal, dependency-free Code 128-B encoder.
 *
 * Code 128 encodes each symbol as 11 modules laid out as six alternating
 * runs (bar, space, bar, space, bar, space) whose widths sum to 11. The stop
 * symbol is the one exception: 13 modules across seven runs. PATTERNS below is
 * that standard table, one entry per code value, written as run widths — index
 * 0..102 are the data values, 103/104/105 are Start A/B/C and 106 is Stop.
 *
 * Only Code Set B is implemented, which covers ASCII 32..126 — everything a
 * brand wordmark needs — with value = charCode - 32. That keeps this small
 * enough to be worth owning rather than pulling in a barcode library, and it
 * emits geometry (not a canvas), which is what the SVG logo needs.
 *
 * The output is genuinely scannable: widths are exact integers in module units,
 * so the caller only has to pick a module width and honour the quiet zones.
 */

const PATTERNS = (
  '212222 222122 222221 121223 121322 131222 122213 122312 132212 221213 221312 231212 ' +
  '112232 122132 122231 113222 123122 123221 223211 221132 221231 213212 223112 312131 ' +
  '311222 321122 321221 312212 322112 322211 212123 212321 232121 111323 131123 131321 ' +
  '112313 132113 132311 211313 231113 231311 112133 112331 132131 113123 113321 133121 ' +
  '313121 211331 231131 213113 213311 213131 311123 311321 331121 312113 312311 332111 ' +
  '314111 221411 431111 111224 111422 121124 121421 141122 141221 112214 112412 122114 ' +
  '122411 142112 142211 241211 221114 413111 241112 134111 111242 121142 121241 114212 ' +
  '124112 124211 411212 421112 421211 212141 214121 412121 111143 111341 131141 114113 ' +
  '114311 411113 411311 113141 114131 311141 411131 211412 211214 211232 2331112'
).split(' ')

const START_B = 104
const STOP = 106

/** Quiet zone required either side of the symbol, in modules. The spec's minimum. */
export const QUIET_ZONE = 10

export interface Code128Bar {
  /** Left edge in module units, measured from the start of the quiet zone. */
  x: number
  /** Bar width in module units (1..4). */
  width: number
}

export interface Code128Symbol {
  bars: Code128Bar[]
  /** Total width in module units, quiet zones included. */
  totalModules: number
}

/**
 * Encode `text` as Code 128-B and return the bars as module-unit geometry,
 * offset by a leading quiet zone. Throws on characters outside ASCII 32..126,
 * which Code Set B cannot represent.
 */
export function encodeCode128B(text: string): Code128Symbol {
  const values: number[] = []
  for (const ch of text) {
    const v = ch.charCodeAt(0) - 32
    if (v < 0 || v > 94) {
      throw new Error(`code128: "${ch}" is outside Code Set B (ASCII 32..126)`)
    }
    values.push(v)
  }

  // Checksum: start value plus each data value weighted by its 1-based position.
  let checksum = START_B
  values.forEach((v, i) => { checksum += v * (i + 1) })
  checksum %= 103

  const codes = [START_B, ...values, checksum, STOP]

  const bars: Code128Bar[] = []
  let x = QUIET_ZONE
  for (const code of codes) {
    const runs = PATTERNS[code]
    for (let i = 0; i < runs.length; i++) {
      const width = Number(runs[i])
      // Even indices are bars, odd are spaces; both advance the cursor.
      if (i % 2 === 0) bars.push({ x, width })
      x += width
    }
  }

  return { bars, totalModules: x + QUIET_ZONE }
}
